import React from 'react'

const INLINE = /(\*\*[^*]+?\*\*|`[^`]+`|\*[^*]+?\*)/g

function inlineNodes(text: string, key: string): React.ReactNode[] {
  const parts = text.split(INLINE)
  return parts.filter((part) => part !== '').map((part, index) => {
    const id = `${key}-${index}`
    if (part.startsWith('**') && part.endsWith('**')) {
      return (
        <strong key={id} className="font-semibold text-blueprint-blue">
          {part.slice(2, -2)}
        </strong>
      )
    }
    if (part.startsWith('`') && part.endsWith('`')) {
      return (
        <code key={id} className="rounded bg-page-bg px-1 py-0.5 font-mono text-[12px] text-blueprint-blue">
          {part.slice(1, -1)}
        </code>
      )
    }
    if (part.startsWith('*') && part.endsWith('*')) {
      return (
        <em key={id} className="italic">
          {part.slice(1, -1)}
        </em>
      )
    }
    return <React.Fragment key={id}>{part}</React.Fragment>
  })
}

function isTableLine(line: string) {
  const trimmed = line.trim()
  return trimmed.startsWith('|') && trimmed.endsWith('|') && trimmed.includes('|', 1)
}

function isSeparator(line: string) {
  return /^\|?\s*:?-{3,}:?\s*(\|\s*:?-{3,}:?\s*)+\|?$/.test(line.trim())
}

function cells(line: string) {
  return line
    .trim()
    .replace(/^\|/, '')
    .replace(/\|$/, '')
    .split('|')
    .map((cell) => cell.trim())
}

function isBullet(line: string) {
  return /^\s*[-*]\s+/.test(line)
}

function isNumbered(line: string) {
  return /^\s*\d+\.\s+/.test(line)
}

function headingLevel(line: string) {
  const match = /^(#{1,4})\s+/.exec(line.trim())
  return match ? match[1].length : 0
}

export function MarkdownBody({ content }: { content: string }) {
  if (!content.trim()) return null
  const lines = content.replace(/\r\n/g, '\n').split('\n')
  const blocks: React.ReactNode[] = []
  let index = 0
  let key = 0

  while (index < lines.length) {
    const line = lines[index]
    if (!line.trim()) {
      index += 1
      continue
    }

    if (isTableLine(line) && index + 1 < lines.length && isSeparator(lines[index + 1])) {
      const header = cells(line)
      index += 2
      const rows: string[][] = []
      while (index < lines.length && isTableLine(lines[index]) && !isSeparator(lines[index])) {
        rows.push(cells(lines[index]))
        index += 1
      }
      blocks.push(
        <div key={key} className="ask-evans-table-wrap my-3 overflow-x-auto rounded-lg border border-[#D8DCE3]">
          <table className="ask-evans-table w-full min-w-[280px] border-collapse text-left text-[12px]">
            <thead className="bg-blueprint-blue text-white">
              <tr>
                {header.map((cell, cellIndex) => (
                  <th
                    key={cellIndex}
                    className="border-r border-white/15 px-3 py-2 font-heading text-[11px] font-semibold uppercase tracking-wide last:border-r-0"
                  >
                    {inlineNodes(cell, `h-${key}-${cellIndex}`)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white">
              {rows.map((row, rowIndex) => (
                <tr key={rowIndex} className="border-b border-[#E2E5EA] last:border-b-0 even:bg-page-bg/70">
                  {header.map((_, cellIndex) => (
                    <td
                      key={cellIndex}
                      className="border-r border-[#E2E5EA] px-3 py-2 align-top text-text-grey last:border-r-0"
                    >
                      {inlineNodes(row[cellIndex] || '', `c-${key}-${rowIndex}-${cellIndex}`)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )
      key += 1
      continue
    }

    if (isBullet(line) || isNumbered(line)) {
      const numbered = isNumbered(line)
      const items: string[] = []
      while (index < lines.length && (numbered ? isNumbered(lines[index]) : isBullet(lines[index]))) {
        items.push(lines[index].replace(numbered ? /^\s*\d+\.\s+/ : /^\s*[-*]\s+/, ''))
        index += 1
      }
      const List = numbered ? 'ol' : 'ul'
      blocks.push(
        <List key={key} className={`mb-2 space-y-1 pl-5 ${numbered ? 'list-decimal' : 'list-disc'}`}>
          {items.map((item, itemIndex) => (
            <li key={itemIndex} className="leading-relaxed">
              {inlineNodes(item, `li-${key}-${itemIndex}`)}
            </li>
          ))}
        </List>
      )
      key += 1
      continue
    }

    const level = headingLevel(line)
    if (level) {
      const text = line.trim().replace(/^#{1,4}\s+/, '')
      const className =
        level <= 2
          ? 'mb-2 mt-3 font-heading text-[14px] font-semibold text-blueprint-blue'
          : 'mb-1.5 mt-2.5 font-heading text-[13px] font-semibold text-blueprint-blue'
      blocks.push(
        <div key={key} className={className}>
          {inlineNodes(text, `hd-${key}`)}
        </div>
      )
      key += 1
      index += 1
      continue
    }

    const paragraph: string[] = []
    while (
      index < lines.length &&
      lines[index].trim() &&
      !isBullet(lines[index]) &&
      !isNumbered(lines[index]) &&
      !headingLevel(lines[index]) &&
      !(isTableLine(lines[index]) && index + 1 < lines.length && isSeparator(lines[index + 1]))
    ) {
      paragraph.push(lines[index].trim())
      index += 1
    }
    blocks.push(
      <p key={key} className="mb-2 last:mb-0">
        {inlineNodes(paragraph.join(' '), `p-${key}`)}
      </p>
    )
    key += 1
  }

  return <div className="ask-evans-prose font-sans text-[13px] leading-relaxed">{blocks}</div>
}
